import React, { useState, useMemo, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  AlertTriangle, ChevronDown, ChevronRight, Check, Info, ListChecks,
  FileWarning, Clock, Search, MessageSquare, GitBranch, Lightbulb, FileText,
} from 'lucide-react';
import { sanitizeEnumValue, enumDisplay } from '../../utils/displayUtils';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';
import { RedactionText } from '../redaction/RedactionText';
import { InlineCitedText } from '../citations/InlineCitedText';
import { EntryActions } from '../ask/EntryActions';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import type { ContextItem } from '../../contexts/AskSidebarContext';
import { useHighlightParam } from '../../hooks/useHighlightParam';
import { fromObjectiveSource, resolveDocumentId } from '../../types/citation';
import type { UnifiedCitation, ObjectiveSource } from '../../types/citation';

// --- Helpers ---

function sev(s: string | undefined): string { return sanitizeEnumValue(s) || 'info'; }

function stripCitations(text: string): string {
  return text.replace(/\s*\[\d+(?:,\s*\d+)*\]/g, '');
}

// --- Finding types ---

interface UnifiedEvidenceFinding {
  id: string;
  source: 'gap' | 'inc';
  tier: SectionTier;
  title: string;
  severity: string;
  data: any;
  catKey: string; // original category key for rendering
}

type SectionTier = 'priority' | 'further' | 'context';

// --- Tier classification ---

function getGapTier(item: any, catKey: string): SectionTier {
  // criticalGaps always have urgency field
  if (catKey === 'criticalGaps') {
    const u = sev(item.urgency);
    if (u === 'high') return 'priority';
    return 'further';
  }
  // Other gap categories use impact field
  const impact = sev(item.impact);
  if (impact === 'critical' || impact === 'high') return 'priority';
  if (impact === 'significant' || impact === 'medium') return 'further';
  return 'context';
}

function getIncTier(item: any, catKey: string): SectionTier {
  if (catKey === 'criticalFindings') return 'priority';
  if (catKey === 'patterns' || catKey === 'recommendations') return 'context';
  const s = sev(item.severity);
  if (s === 'critical') return 'priority';
  if (s === 'significant') return 'further';
  return 'context';
}

// --- Gap categories (from GapAnalysisPanel) ---

interface GapCatConfig {
  key: string;
  label: string;
  severityField: string;
}

const GAP_CATEGORIES: GapCatConfig[] = [
  { key: 'criticalGaps',        label: 'Critical Gaps',         severityField: 'urgency' },
  { key: 'missingDocuments',    label: 'Missing Documents',     severityField: 'impact' },
  { key: 'incompleteDocuments', label: 'Incomplete Documents',  severityField: 'impact' },
  { key: 'timelineGaps',       label: 'Timeline Gaps',         severityField: '' },
  { key: 'informationGaps',    label: 'Information Gaps',      severityField: '' },
  { key: 'recommendations',    label: 'Recommendations',       severityField: 'priority' },
];

function getGapItems(content: any, key: string): any[] {
  if (key === 'criticalGaps') return content.criticalGaps || [];
  if (key === 'recommendations') return content.recommendations || [];
  return content.categories?.[key] || [];
}

function getGapTitle(item: any, catKey: string): string {
  switch (catKey) {
    case 'criticalGaps': return item.gap || '';
    case 'missingDocuments': return item.documentType || '';
    case 'incompleteDocuments': return item.documentName || '';
    case 'timelineGaps': return item.duration || '';
    case 'informationGaps': return item.description || '';
    case 'recommendations': return typeof item === 'string' ? item : item.action || '';
    default: return '';
  }
}

// --- Inc categories (from InconsistencyPanel) ---

function getIncItems(content: any, key: string): any[] {
  return content[key] || [];
}

function getIncTitle(item: any, catKey: string): string {
  switch (catKey) {
    case 'criticalFindings': return item.finding || '';
    case 'inconsistencies': return item.description || '';
    case 'patterns': return item.pattern || '';
    case 'recommendations': return typeof item === 'string' ? item : item.action || item.recommendation || item;
    default: return '';
  }
}

// --- Build findings ---

function extractEvidenceFindings(gapContent: any | null, incContent: any | null): UnifiedEvidenceFinding[] {
  const findings: UnifiedEvidenceFinding[] = [];
  let idx = 0;

  if (gapContent) {
    for (const cat of GAP_CATEGORIES) {
      const items = getGapItems(gapContent, cat.key);
      for (const item of items) {
        const tier = getGapTier(item, cat.key);
        const severity = cat.severityField ? (item[cat.severityField] || '') : '';
        findings.push({
          id: `gap-${cat.key}-${idx++}`,
          source: 'gap',
          tier,
          title: stripCitations(getGapTitle(item, cat.key)),
          severity,
          data: item,
          catKey: cat.key,
        });
      }
    }
  }

  if (incContent) {
    for (const catKey of ['criticalFindings', 'inconsistencies', 'patterns', 'recommendations']) {
      const items = getIncItems(incContent, catKey);
      for (const item of items) {
        const tier = getIncTier(item, catKey);
        const severity = item.severity || '';
        findings.push({
          id: `inc-${catKey}-${idx++}`,
          source: 'inc',
          tier,
          title: stripCitations(getIncTitle(item, catKey)),
          severity,
          data: item,
          catKey,
        });
      }
    }
  }

  return findings;
}

// --- Section config ---

const SECTION_CONFIG = {
  priority: {
    label: 'Priority Review',
    description: 'Critical gaps and inconsistencies requiring immediate attention',
    borderColor: 'border-red-200 dark:border-red-800',
    headerBg: 'bg-red-50/50 dark:bg-red-900/10',
    icon: <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />,
  },
  further: {
    label: 'Further Consideration',
    description: 'Gaps and inconsistencies that may warrant review',
    borderColor: 'border-amber-200 dark:border-amber-800',
    headerBg: 'bg-amber-50/50 dark:bg-amber-900/10',
    icon: <Info className="w-4 h-4 text-amber-600 dark:text-amber-400" />,
  },
  context: {
    label: 'Reviewed & Noted',
    description: 'Minor items noted with no significant concerns',
    borderColor: 'border-gray-200 dark:border-medical-700',
    headerBg: 'bg-gray-50/50 dark:bg-medical-800/50',
    icon: <Check className="w-4 h-4 text-green-600 dark:text-green-400" />,
  },
} as const;

// --- Citation resolution ---

function buildCitations(content: any, documents: any[]): UnifiedCitation[] {
  if (!content?.sources || !Array.isArray(content.sources)) return [];
  return content.sources
    .filter((s: any) => s && typeof s === 'object' && s.documentName)
    .map((s: any) => {
      const documentId = s.documentId || resolveDocumentId(s.documentName, documents);
      return fromObjectiveSource({ ...s, documentId } as ObjectiveSource);
    });
}

// --- Component ---

export interface UnifiedEvidenceFindingsHandle {
  scrollToSection: (tier: SectionTier) => void;
}

interface UnifiedEvidenceFindingsProps {
  gapContent: any | null;
  incContent: any | null;
  documents: any[];
}

export const UnifiedEvidenceFindings = forwardRef<UnifiedEvidenceFindingsHandle, UnifiedEvidenceFindingsProps>(({
  gapContent, incContent, documents,
}, ref) => {
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<SectionTier>>(new Set(['priority', 'further', 'context']));
  const [notesCollapsed, setNotesCollapsed] = useState(false);
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const gapCitations = useMemo(() => buildCitations(gapContent, documents), [gapContent, documents]);
  const incCitations = useMemo(() => buildCitations(incContent, documents), [incContent, documents]);

  const findings = useMemo(() => extractEvidenceFindings(gapContent, incContent), [gapContent, incContent]);

  const tieredFindings = useMemo(() => {
    const tiers: Record<SectionTier, UnifiedEvidenceFinding[]> = { priority: [], further: [], context: [] };
    for (const f of findings) {
      tiers[f.tier].push(f);
    }
    return tiers;
  }, [findings]);

  const toggleFinding = useCallback((id: string) => {
    setExpandedFindings(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleSection = useCallback((tier: SectionTier) => {
    setCollapsedSections(prev => { const n = new Set(prev); if (n.has(tier)) n.delete(tier); else n.add(tier); return n; });
  }, []);

  const getCitations = useCallback((source: 'gap' | 'inc') => source === 'gap' ? gapCitations : incCitations, [gapCitations, incCitations]);

  const scrollToSection = useCallback((tier: SectionTier) => {
    const el = sectionRefs.current.get(tier);
    if (el) {
      setCollapsedSections(prev => { const n = new Set(prev); n.delete(tier); return n; });
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useImperativeHandle(ref, () => ({ scrollToSection }), [scrollToSection]);

  // Auto-expand and scroll to a highlighted finding from URL param
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId || findings.length === 0) return;
    for (const [tier, items] of Object.entries(tieredFindings) as [SectionTier, UnifiedEvidenceFinding[]][]) {
      if (items.some(f => f.id === highlightId)) {
        setCollapsedSections(prev => { const n = new Set(prev); n.delete(tier); return n; });
        setExpandedFindings(prev => { const n = new Set(prev); n.add(highlightId); return n; });
        requestAnimationFrame(() => scrollToHighlighted());
        return;
      }
    }
  }, [highlightId, findings, tieredFindings, scrollToHighlighted]);

  // Merged limitations
  const limitations = [
    ...(gapContent?.limitations || []),
    ...(incContent?.limitations || []),
  ];
  // Metadata
  const gapMeta = gapContent?.metadata;
  const incMeta = incContent?.metadata;

  return (
    <div className="space-y-4">
      {/* Tiered sections */}
      {(['priority', 'further', 'context'] as SectionTier[]).map(tier => {
        const sectionFindings = tieredFindings[tier];
        if (sectionFindings.length === 0 && tier !== 'priority') return null;

        const config = SECTION_CONFIG[tier];
        const isCollapsed = collapsedSections.has(tier);

        // Split by source for sub-headers
        const gapFindings = sectionFindings.filter(f => f.source === 'gap');
        const incFindings = sectionFindings.filter(f => f.source === 'inc');

        return (
          <div
            key={tier}
            ref={el => { if (el) sectionRefs.current.set(tier, el); }}
            className={`scroll-mt-4 border rounded-lg overflow-hidden ${config.borderColor}`}
          >
            {/* Section header */}
            <button
              onClick={() => toggleSection(tier)}
              className={`w-full flex items-center justify-between p-3.5 transition-colors ${config.headerBg}`}
            >
              <div className="flex items-center gap-2.5">
                {config.icon}
                <div className="text-left">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-medical-100">{config.label}</h4>
                  <p className="text-xs text-gray-500 dark:text-medical-400">
                    {sectionFindings.length === 0
                      ? 'No issues flagged for priority review'
                      : `${sectionFindings.length} ${sectionFindings.length === 1 ? 'finding' : 'findings'} — ${config.description.toLowerCase()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-medical-400">{sectionFindings.length}</span>
                {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </button>

            {/* LLM tier summary — always visible, never collapses */}
            {sectionFindings.length > 0 && (() => {
              const gapTier = gapContent?.tierSummaries?.[tier];
              const incTier = incContent?.tierSummaries?.[tier];
              const tierText = gapTier || incTier;
              const fallbackText = tier === 'priority' ? (gapContent?.summary || incContent?.summary) : null;
              const summaryText = tierText || fallbackText;
              if (!summaryText) return null;
              return (
                <div className={`px-4 py-3 ${!isCollapsed ? 'border-b border-gray-100 dark:border-medical-700' : ''}`}>
                  <p className="text-sm text-gray-600 dark:text-medical-400 leading-relaxed">
                    <MarkdownWithCitations
                      content={stripCitations(summaryText)}
                      citations={[...gapCitations, ...incCitations]}
                    />
                  </p>
                </div>
              );
            })()}

            {/* Collapsed hint */}
            {isCollapsed && sectionFindings.length > 0 && (
              <button
                onClick={() => toggleSection(tier)}
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50/50 dark:hover:bg-medical-700/10 transition-colors flex items-center gap-2"
              >
                <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-medical-500" />
                <span className="text-sm text-cyan-600 dark:text-cyan-400 font-medium">
                  Show {sectionFindings.length} {sectionFindings.length === 1 ? 'finding' : 'findings'}
                </span>
              </button>
            )}

            {/* Expanded findings list */}
            {!isCollapsed && (
              <div className="space-y-0.5">
                {sectionFindings.length === 0 && tier === 'priority' && (
                  <div className="text-center py-6 text-sm text-gray-500 dark:text-medical-400">
                    <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    No issues flagged for priority review.
                  </div>
                )}

                {/* Gap findings sub-group */}
                {gapFindings.length > 0 && (
                  <>
                    {incFindings.length > 0 && (
                      <div className="px-4 py-2 bg-gray-50/80 dark:bg-medical-800/80">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-medical-500">
                          Documentary Gaps ({gapFindings.length})
                        </p>
                      </div>
                    )}
                    {gapFindings.map(finding => (
                      <EvidenceFindingCard
                        key={finding.id}
                        finding={finding}
                        isExpanded={expandedFindings.has(finding.id)}
                        onToggle={() => toggleFinding(finding.id)}
                        citations={getCitations(finding.source)}
                      />
                    ))}
                  </>
                )}

                {/* Inconsistency findings sub-group */}
                {incFindings.length > 0 && (
                  <>
                    {gapFindings.length > 0 && (
                      <div className="px-4 py-2 bg-gray-50/80 dark:bg-medical-800/80">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-medical-500">
                          Inconsistencies ({incFindings.length})
                        </p>
                      </div>
                    )}
                    {incFindings.map(finding => (
                      <EvidenceFindingCard
                        key={finding.id}
                        finding={finding}
                        isExpanded={expandedFindings.has(finding.id)}
                        onToggle={() => toggleFinding(finding.id)}
                        citations={getCitations(finding.source)}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Analysis Notes */}
      {(limitations.length > 0 || gapMeta || incMeta) && (
        <div className="border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setNotesCollapsed(prev => !prev)}
            className="w-full flex items-center justify-between p-3.5 bg-gray-50/50 dark:bg-medical-800/50"
          >
            <div className="flex items-center gap-2.5">
              <ListChecks className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <div className="text-left">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-medical-100">Analysis Notes</h4>
                <p className="text-xs text-gray-500 dark:text-medical-400">
                  {[
                    gapMeta?.documentsAnalysed || gapMeta?.documentsAnalyzed ? `${gapMeta.documentsAnalysed || gapMeta.documentsAnalyzed} documents analysed` : null,
                    incMeta?.documentsAnalysed || incMeta?.documentsAnalyzed ? `${incMeta.documentsAnalysed || incMeta.documentsAnalyzed} documents cross-referenced` : null,
                    incMeta?.inconsistenciesFound ? `${incMeta.inconsistenciesFound} inconsistencies found` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            {notesCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {!notesCollapsed && <div className="px-4 pb-3 space-y-2">
            {limitations.length > 0 && (
              <ul className="text-xs text-gray-500 dark:text-medical-400 space-y-1">
                {limitations.map((l: string, i: number) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-gray-400 mt-0.5">•</span>
                    <span>{typeof l === 'string' ? l : JSON.stringify(l)}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-400 dark:text-medical-500 italic pt-2 border-t border-gray-100 dark:border-medical-700">
              This analysis identifies areas for expert review. It does not constitute clinical or legal opinion.
            </p>
          </div>}
        </div>
      )}
    </div>
  );
});

// --- Finding card ---

interface EvidenceFindingCardProps {
  finding: UnifiedEvidenceFinding;
  isExpanded: boolean;
  onToggle: () => void;
  citations: UnifiedCitation[];
}

const EvidenceFindingCard: React.FC<EvidenceFindingCardProps> = ({
  finding, isExpanded, onToggle, citations,
}) => {
  const SEVERITY_BADGES: Record<string, string> = {
    critical: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    significant: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    minor: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
    high: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    medium: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    low: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
    explainable: 'bg-gray-100 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400',
    info: 'bg-gray-100 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300',
  };

  const badgeClass = SEVERITY_BADGES[sev(finding.severity)] || SEVERITY_BADGES.info;
  const sourceLabel = finding.source === 'gap' ? 'Gap Analysis' : 'Inconsistency Detection';
  const contextType: ContextItem['type'] = finding.source === 'gap' ? 'gap' : 'inconsistency';
  const { isEventMarked } = useKeyFindings();
  const isMarked = isEventMarked(finding.id);

  return (
    <div data-event-id={finding.id} className={`transition-all duration-150 rounded-lg border ${isMarked ? 'border-amber-300 dark:border-amber-600 shadow-sm' : 'border-transparent'} ${isExpanded ? 'bg-gray-50/50 dark:bg-medical-700/20' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-2 p-3 text-left hover:bg-gray-50 dark:hover:bg-medical-700/30 transition-colors group rounded-lg"
      >
        {isExpanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          : <ChevronRight className="w-4 h-4 text-gray-300 dark:text-medical-600 group-hover:text-gray-500 dark:group-hover:text-medical-400 flex-shrink-0 mt-0.5 transition-colors" />}
        {finding.severity && (
          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${badgeClass}`}>
            {finding.severity}
          </span>
        )}
        <span className={`text-sm text-gray-900 dark:text-medical-100 flex-1 ${isExpanded ? 'font-medium' : ''}`}>
          <InlineCitedText text={finding.title} citations={citations} />
        </span>
        <EntryActions
          compact
          item={{
            id: finding.id,
            type: contextType,
            summary: finding.title.slice(0, 80),
            source: `${sourceLabel} — ${finding.catKey}`,
            data: finding.data,
          }}
        />
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 pt-0 ml-6 space-y-2 text-sm text-gray-700 dark:text-medical-300">
          {finding.source === 'gap' && renderGapDetail(finding.data, finding.catKey, citations)}
          {finding.source === 'inc' && renderIncDetail(finding.data, finding.catKey, citations)}
        </div>
      )}
    </div>
  );
};

// --- Gap detail renderers ---

function renderGapDetail(item: any, catKey: string, citations: UnifiedCitation[]): React.ReactNode {
  const Cited: React.FC<{text?: string}> = ({ text }) =>
    text ? <MarkdownWithCitations content={text} citations={citations} /> : null;

  switch (catKey) {
    case 'criticalGaps':
      return (
        <>
          {item.why && <div><span className="font-medium text-gray-900 dark:text-medical-100">Why: </span><Cited text={item.why} /></div>}
          {item.impact && <div><span className="font-medium text-gray-900 dark:text-medical-100">Impact: </span><Cited text={item.impact} /></div>}
        </>
      );
    case 'missingDocuments':
      return (
        <>
          {item.expectedDate && <p className="text-xs text-gray-500 dark:text-medical-400">Expected: {item.expectedDate}</p>}
          {item.significance && <Cited text={item.significance} />}
          {item.possibleReasons && item.possibleReasons.length > 0 && (
            <div className="mt-1">
              <span className="text-xs font-medium text-gray-500 dark:text-medical-400">Possible reasons:</span>
              <ul className="text-xs text-gray-500 dark:text-medical-400 list-disc list-inside mt-0.5">
                {item.possibleReasons.map((r: string, i: number) => <li key={i}><Cited text={r} /></li>)}
              </ul>
            </div>
          )}
          {item.recommendations && (
            <div className="mt-1 font-medium text-gray-900 dark:text-medical-100">Recommendation: <Cited text={item.recommendations} /></div>
          )}
        </>
      );
    case 'incompleteDocuments':
      return (
        <>
          {item.pageNumber && <p className="text-xs text-gray-500 dark:text-medical-400">Page {item.pageNumber}</p>}
          {item.missingElements && item.missingElements.length > 0 && (
            <ul className="list-disc list-inside space-y-0.5">
              {item.missingElements.map((el: string, i: number) => <li key={i}><Cited text={el} /></li>)}
            </ul>
          )}
          {item.significance && <div className="mt-1"><Cited text={item.significance} /></div>}
        </>
      );
    case 'timelineGaps':
      return (
        <>
          {(item.startTime || item.endTime) && (
            <p className="text-xs text-gray-500 dark:text-medical-400"><RedactionText text={`${item.startTime} → ${item.endTime}`} /></p>
          )}
          {item.expectedDocumentation && <div><span className="font-medium text-gray-900 dark:text-medical-100">Expected: </span><Cited text={item.expectedDocumentation} /></div>}
          {item.significance && <Cited text={item.significance} />}
          {item.clinicalContext && <div className="opacity-80"><span className="font-medium text-gray-900 dark:text-medical-100">Context: </span><Cited text={item.clinicalContext} /></div>}
        </>
      );
    case 'informationGaps':
      return (
        <>
          {item.category && <span className="text-[10px] uppercase font-bold opacity-50">{enumDisplay(item.category)}</span>}
          {item.relevance && <Cited text={item.relevance} />}
          {item.impact && <div><span className="font-medium text-gray-900 dark:text-medical-100">Impact: </span><Cited text={item.impact} /></div>}
          {item.possibleSources && item.possibleSources.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-medical-400">Sources: <RedactionText text={item.possibleSources.join(', ')} /></p>
          )}
        </>
      );
    case 'recommendations':
      return (
        <>
          {item.rationale && <Cited text={item.rationale} />}
        </>
      );
    default:
      return null;
  }
}

// --- Inconsistency detail renderers ---

function renderIncDetail(item: any, catKey: string, citations: UnifiedCitation[]): React.ReactNode {
  switch (catKey) {
    case 'criticalFindings':
      return (
        <>
          {item.explanation && <MarkdownWithCitations content={item.explanation} citations={citations} />}
          {item.requiresUrgentReview && (
            <p className="text-xs font-semibold text-red-600 dark:text-red-400 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" /> Requires urgent review
            </p>
          )}
        </>
      );
    case 'inconsistencies':
      return (
        <>
          {item.conflictingStatements && item.conflictingStatements.length > 0 && (
            <div className="space-y-2 mt-1">
              <span className="text-xs font-medium text-gray-500 dark:text-medical-400">Conflicting statements:</span>
              {item.conflictingStatements.map((stmt: any, i: number) => (
                <div key={i} className="pl-3 border-l-2 border-amber-300 dark:border-amber-700">
                  <div className="font-medium text-gray-900 dark:text-medical-100"><MarkdownWithCitations content={stmt.statement} citations={citations} /></div>
                  <p className="text-xs text-gray-500 dark:text-medical-400 mt-0.5">
                    {stmt.documentName}{stmt.pageNumber ? `, p.${stmt.pageNumber}` : ''}
                    {stmt.author ? ` — ${stmt.author}` : ''}
                  </p>
                  {stmt.excerpt && <p className="text-xs italic mt-0.5 opacity-80">"{stmt.excerpt}"</p>}
                </div>
              ))}
            </div>
          )}
          {item.type && (
            <p className="text-xs text-gray-500 dark:text-medical-400">Type: {item.type}</p>
          )}
          {item.analysis && <div className="mt-1"><MarkdownWithCitations content={item.analysis} citations={citations} /></div>}
          {item.potentialImpact && <div><span className="font-medium text-gray-900 dark:text-medical-100">Impact: </span><MarkdownWithCitations content={item.potentialImpact} citations={citations} /></div>}
          {item.recommendedAction && <div><span className="font-medium text-gray-900 dark:text-medical-100">Action: </span><MarkdownWithCitations content={item.recommendedAction} citations={citations} /></div>}
          {item.possibleExplanations && item.possibleExplanations.length > 0 && (
            <div className="mt-1">
              <span className="text-xs font-medium text-gray-500 dark:text-medical-400">Possible explanations:</span>
              <ul className="text-xs text-gray-500 dark:text-medical-400 list-disc list-inside mt-0.5">
                {item.possibleExplanations.map((exp: string, i: number) => <li key={i}><MarkdownWithCitations content={exp} citations={citations} /></li>)}
              </ul>
            </div>
          )}
          {item.confidence && (
            <p className="text-xs text-gray-500 dark:text-medical-400">Confidence: {item.confidence}</p>
          )}
        </>
      );
    case 'patterns':
      return (
        <>
          {item.significance && <MarkdownWithCitations content={item.significance} citations={citations} />}
          {item.frequency && <p className="text-xs text-gray-500 dark:text-medical-400">Frequency: {item.frequency} occurrences</p>}
        </>
      );
    case 'recommendations':
      return typeof item === 'string' ? null : (
        <>
          {item.rationale && <MarkdownWithCitations content={item.rationale} citations={citations} />}
        </>
      );
    default:
      return null;
  }
}
